// forked https://github.com/samhirtarif/react-audio-recorder

import React, { Suspense, useEffect, useMemo, useState, type ReactElement } from "react";
import { Check, Mic, Pause, Play, Trash2 } from "lucide-react";

import { computeAudioMetering } from "@fitness-app/client-shared/features/chat/audio-metering";
import { buildAudioWaveBars } from "@fitness-app/client-shared/features/chat/audio-wave-bars";
import { useAudioPlayer } from "@fitness-app/client-shared/features/chat/use-audio-player";
import { cn } from "@fitness-app/utils/src/styles/cn";

import { AudioWaveform } from "~/components/Upload/AudioWaveform";
import { type Props } from "./types";
import useAudioRecorder from "./useAudioRecorder";

const LiveAudioVisualizer = React.lazy(async () => {
  const { LiveAudioVisualizer } = await import("react-audio-visualize");
  return { default: LiveAudioVisualizer };
});

/**
 * Usage: https://github.com/samhirtarif/react-audio-recorder#audiorecorder-component
 *
 *
 * @prop `onRecordingComplete` Method that gets called when save recording option is clicked
 * @prop `recorderControls` Externally initilize hook and pass the returned object to this param, this gives your control over the component from outside the component.
 * https://github.com/samhirtarif/react-audio-recorder#combine-the-useaudiorecorder-hook-and-the-audiorecorder-component
 * @prop `audioTrackConstraints`: Takes a {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackSettings#instance_properties_of_audio_tracks subset} of `MediaTrackConstraints` that apply to the audio track
 * @prop `onNotAllowedOrFound`: A method that gets called when the getUserMedia promise is rejected. It receives the DOMException as its input.
 * @prop `downloadOnSavePress` If set to `true` the file gets downloaded when save recording is pressed. Defaults to `false`
 * @prop `downloadFileExtension` File extension for the audio filed that gets downloaded. Defaults to `mp3`. Allowed values are `mp3`, `wav` and `webm`
 */

export const getRecordTime = (recordingTime: number): string =>
  `${Math.floor(recordingTime / 60)}:${String(recordingTime % 60).padStart(2, "0")}`;

export const convertToDownloadFileExtension = async (webmBlob: Blob, downloadFileExtension = "webm"): Promise<Blob> => {
  const FFmpeg = await import("@ffmpeg/ffmpeg");
  // @ts-expect-error ignore
  const ffmpeg = FFmpeg.createFFmpeg({ log: false });
  await ffmpeg.load();

  const inputName = "input.webm";
  const outputName = `output.${downloadFileExtension}`;

  ffmpeg.FS("writeFile", inputName, new Uint8Array(await webmBlob.arrayBuffer()));

  await ffmpeg.run("-i", inputName, outputName);

  const outputData = ffmpeg.FS("readFile", outputName);
  const outputBlob = new Blob([outputData.buffer], {
    type: `audio/${downloadFileExtension}`,
  });

  return outputBlob;
};

export const transformMediaFormat = async (blob: Blob, downloadFileExtension = "webm"): Promise<Blob> => {
  if (!crossOriginIsolated && downloadFileExtension !== "webm") {
    // eslint-disable-next-line no-console
    console.warn(
      `This website is not "cross-origin isolated". Audio will be downloaded in webm format, since mp3/wav encoding requires cross origin isolation. Please visit https://web.dev/cross-origin-isolation-guide/ and https://web.dev/coop-coep/ for information on how to make your website "cross-origin isolated"`,
    );
  }
  const fileExt = crossOriginIsolated ? downloadFileExtension : "webm";

  const downloadBlob = crossOriginIsolated ? await convertToDownloadFileExtension(blob, fileExt) : blob;

  return downloadBlob;
};

type RoundButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "neutral" | "danger";
};

const RoundButton = ({ variant = "neutral", className, ...props }: RoundButtonProps): ReactElement => (
  <button
    type="button"
    className={cn(
      "flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 p-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 active:scale-95",
      variant === "primary" && "bg-blue-500 text-white hover:bg-blue-600",
      variant === "neutral" && "bg-white text-gray-700 shadow-sm hover:bg-gray-50",
      variant === "danger" && "bg-transparent text-red-500 hover:bg-red-50",
      className,
    )}
    {...props}
  />
);

// `min-w-0` so the pill can shrink inside the input row - in the trainee-profile drawer it sits
// next to the send button, and a fixed minimum width pushed that button out of the panel.
const shellClassName =
  "flex min-h-[48px] w-full min-w-0 items-center gap-x-2.5 rounded-2xl bg-gray-100 py-1.5 pl-2 pr-2";

/**
 * Width of the live visualizer's canvas: whatever the strip between timer and buttons has. A
 * callback ref, because the strip unmounts while the preview shows and comes back on "delete" -
 * an observer attached once at mount would keep watching the detached node.
 */
const useMeasuredWidth = (): [(node: HTMLSpanElement | null) => void, number] => {
  const [node, setNode] = useState<HTMLSpanElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!node) {
      return undefined;
    }
    setWidth(Math.floor(node.getBoundingClientRect().width));
    const observer = new ResizeObserver(([entry]) => setWidth(Math.floor(entry?.contentRect.width ?? 0)));
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, width];
};

interface RecordingPreviewProps {
  blob: Blob;
  url: string;
  durationSeconds: number | null;
  onDelete: () => void;
}

/** The finished take, in the same shape it will have once sent, so what you hear is what they get. */
const RecordingPreview = ({ blob, url, durationSeconds, onDelete }: RecordingPreviewProps): ReactElement => {
  const [metering, setMetering] = useState<number[] | null>(null);
  const bars = useMemo(() => buildAudioWaveBars(metering), [metering]);
  const { isPlaying, currentTime, duration, progress, toggle, seek } = useAudioPlayer({
    src: url,
    fallbackDurationSeconds: durationSeconds,
  });

  useEffect(() => {
    let cancelled = false;
    computeAudioMetering(blob)
      .then((values) => {
        if (!cancelled) {
          setMetering(values);
        }
      })
      // Undecodable blob (exotic codec) keeps the fallback envelope; playback is unaffected.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [blob]);

  const shownTime = isPlaying || currentTime > 0 ? currentTime : duration;

  return (
    <div className={shellClassName} data-testid="audio_recorder">
      <RoundButton variant="primary" onClick={toggle} aria-label={isPlaying ? "Zatrzymaj" : "Odtwórz"}>
        {isPlaying ? (
          <Pause size={15} fill="currentColor" strokeWidth={0} />
        ) : (
          <Play size={15} fill="currentColor" strokeWidth={0} className="ml-0.5" />
        )}
      </RoundButton>
      <AudioWaveform
        bars={bars}
        progress={progress}
        seek={seek}
        playedClassName="bg-blue-500"
        unplayedClassName="bg-gray-400/70"
      />
      <span
        className="min-w-[2.5rem] text-right text-xs font-semibold tabular-nums text-gray-800"
        data-testid="ar_timer"
      >
        {getRecordTime(Math.round(shownTime))}
      </span>
      <RoundButton variant="danger" onClick={onDelete} aria-label="Usuń nagranie">
        <Trash2 size={16} />
      </RoundButton>
    </div>
  );
};

const AudioRecorder: (props: Props) => ReactElement = ({
  onRecordingComplete,
  onDiscard,
  onNotAllowedOrFound,
  recorderControls,
  audioTrackConstraints,
  downloadOnSavePress = false,
  downloadFileExtension = "webm",
  mediaRecorderOptions,
}: Props) => {
  const [savedBlob, setBlob] = useState<Blob>();
  const [savedRecordTime, setRecordedTime] = useState<number | null>(null);
  const {
    startRecording,
    stopRecording,
    togglePauseResume,
    recordingBlob,
    isRecording,
    isPaused,
    recordingTime,
    mediaRecorder,
    // eslint-disable-next-line react-hooks/rules-of-hooks -- upstream API: the hook runs only when no controls are injected, stable per mount
  } = recorderControls ?? useAudioRecorder(audioTrackConstraints, onNotAllowedOrFound, mediaRecorderOptions);
  const [shouldSave, setShouldSave] = useState(false);
  const [visualizerRef, visualizerWidth] = useMeasuredWidth();

  const stopAudioRecorder: (save?: boolean) => void = (save = true) => {
    setShouldSave(save);
    if (!save) {
      setBlob(undefined);
      setRecordedTime(null);
    } else {
      setRecordedTime(recordingTime);
    }

    stopRecording();
  };

  // The bin means "I do not want this": drop the take and hand control back to the input,
  // instead of leaving an empty recorder that answers with nothing.
  const discard = () => {
    stopAudioRecorder(false);
    onDiscard?.();
  };

  const downloadBlob = async (blob: Blob): Promise<void> => {
    if (!crossOriginIsolated && downloadFileExtension !== "webm") {
      // eslint-disable-next-line no-console
      console.warn(
        `This website is not "cross-origin isolated". Audio will be downloaded in webm format, since mp3/wav encoding requires cross origin isolation. Please visit https://web.dev/cross-origin-isolation-guide/ and https://web.dev/coop-coep/ for information on how to make your website "cross-origin isolated"`,
      );
    }
    const fileExt = crossOriginIsolated ? downloadFileExtension : "webm";

    const downloadBlob = crossOriginIsolated ? await convertToDownloadFileExtension(blob, fileExt) : blob;
    const url = URL.createObjectURL(downloadBlob);

    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    a.download = `audio.${fileExt}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  useEffect(() => {
    // Only a take the user finished with the check button is handed over. The upstream
    // condition also fired for injected `recorderControls`, so stopping via the bin or the mic
    // toggle still uploaded the blob - with a zero duration, and empty when stopped at once.
    if (!shouldSave || recordingBlob == null || onRecordingComplete == null) {
      return;
    }
    if (recordingBlob.size === 0) {
      setShouldSave(false);
      return;
    }
    // The timer ticks once a second, so a take finished within the first second reads 0 even
    // though the blob holds audio; it is kept and reported as a one-second recording.
    onRecordingComplete(recordingBlob, Math.max(savedRecordTime ?? 0, 1));
    setBlob(recordingBlob);
    if (downloadOnSavePress) {
      void downloadBlob(recordingBlob);
    }
  }, [recordingBlob]);

  // Created and revoked in the same effect: a memoised URL revoked by a separate cleanup was
  // already dead when StrictMode re-ran the effects in dev, and play then failed silently.
  const [stableUrl, setStableUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!savedBlob) {
      setStableUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(savedBlob);
    setStableUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [savedBlob]);

  if (savedBlob && stableUrl) {
    return <RecordingPreview blob={savedBlob} url={stableUrl} durationSeconds={savedRecordTime} onDelete={discard} />;
  }

  const live = isRecording && !isPaused;

  return (
    <div className={cn(shellClassName, "pl-3")} data-testid="audio_recorder">
      <span
        className={cn("h-2.5 w-2.5 shrink-0 rounded-full", live ? "animate-pulse bg-red-500" : "bg-gray-400")}
        aria-hidden
      />
      <span className="min-w-[2.5rem] text-xs font-semibold tabular-nums text-gray-800" data-testid="ar_timer">
        {getRecordTime(recordingTime)}
      </span>
      <span ref={visualizerRef} className="flex h-8 min-w-0 flex-1 items-center overflow-hidden">
        {mediaRecorder && visualizerWidth > 0 ? (
          <Suspense fallback={<></>}>
            <LiveAudioVisualizer
              mediaRecorder={mediaRecorder}
              barWidth={3}
              gap={2}
              width={visualizerWidth}
              height={28}
              barColor="#3b82f6"
              fftSize={512}
              maxDecibels={-10}
              minDecibels={-80}
              smoothingTimeConstant={0.4}
            />
          </Suspense>
        ) : (
          <span className="truncate text-sm text-gray-500">Zacznij nagrywanie</span>
        )}
      </span>

      {isPaused || recordingTime === 0 ? (
        <RoundButton onClick={isPaused ? togglePauseResume : startRecording} aria-label="Nagrywaj">
          <Mic size={16} />
        </RoundButton>
      ) : (
        <RoundButton onClick={togglePauseResume} aria-label="Wstrzymaj nagrywanie">
          <Pause size={15} fill="currentColor" strokeWidth={0} />
        </RoundButton>
      )}

      {isRecording ? (
        <RoundButton variant="primary" onClick={() => stopAudioRecorder(true)} aria-label="Zakończ nagranie">
          <Check size={16} strokeWidth={3} />
        </RoundButton>
      ) : null}

      <RoundButton variant="danger" onClick={discard} aria-label="Odrzuć nagranie">
        <Trash2 size={16} />
      </RoundButton>
    </div>
  );
};

export default AudioRecorder;
